<template>
  <div class="version-compare-container">
    <a-page-header 
      :title="`版本对比 - v${version1} vs v${version2}`" 
      @back="() => $router.back()"
    >
      <template #extra>
        <a-space>
          <span>v{{ version1 }}</span>
          <SwapOutlined />
          <span>v{{ version2 }}</span>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <!-- 版本信息卡片 -->
      <a-row :gutter="16" style="margin-top: 16px">
        <a-col :span="12">
          <a-card size="small" :title="`v${version1} - 旧版本`" class="version-card old-version">
            <a-descriptions :column="1" size="small" v-if="diffData?.version1">
              <a-descriptions-item label="版本说明">
                {{ diffData.version1.commit_message || '-' }}
              </a-descriptions-item>
              <a-descriptions-item label="创建时间">
                {{ formatDate(diffData.version1.create_time) }}
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card size="small" :title="`v${version2} - 新版本`" class="version-card new-version">
            <a-descriptions :column="1" size="small" v-if="diffData?.version2">
              <a-descriptions-item label="版本说明">
                {{ diffData.version2.commit_message || '-' }}
              </a-descriptions-item>
              <a-descriptions-item label="创建时间">
                {{ formatDate(diffData.version2.create_time) }}
              </a-descriptions-item>
            </a-descriptions>
          </a-card>
        </a-col>
      </a-row>

      <!-- 字段差异 -->
      <a-card title="字段差异" style="margin-top: 16px" v-if="diffData?.diffs?.fields">
        <a-table
          :columns="fieldColumns"
          :data-source="changedFields"
          :pagination="false"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'old_value'">
              <span class="diff-old">{{ formatValue(record.old_value) }}</span>
            </template>
            <template v-else-if="column.key === 'new_value'">
              <span class="diff-new">{{ formatValue(record.new_value) }}</span>
            </template>
          </template>
        </a-table>
      </a-card>

      <!-- 标签差异 -->
      <a-card title="标签差异" style="margin-top: 16px" v-if="diffData?.diffs?.tags">
        <a-row :gutter="16">
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #52c41a;">新增标签</h4>
              <a-tag v-for="tag in diffData.diffs.tags.added" :key="tag.id" color="green">
                {{ tag.tag_value || tag.tag_name }}
              </a-tag>
              <span v-if="!diffData.diffs.tags.added?.length" style="color: #999;">无</span>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #ff4d4f;">删除标签</h4>
              <a-tag v-for="tag in diffData.diffs.tags.removed" :key="tag.id" color="red">
                {{ tag.tag_value || tag.tag_name }}
              </a-tag>
              <span v-if="!diffData.diffs.tags.removed?.length" style="color: #999;">无</span>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #666;">未变标签</h4>
              <a-tag v-for="tag in diffData.diffs.tags.unchanged" :key="tag.id">
                {{ tag.tag_value || tag.tag_name }}
              </a-tag>
              <span v-if="!diffData.diffs.tags.unchanged?.length" style="color: #999;">无</span>
            </div>
          </a-col>
        </a-row>
      </a-card>

      <!-- 步骤差异 -->
      <a-card title="步骤差异" style="margin-top: 16px" v-if="diffData?.diffs?.steps">
        <a-row :gutter="16">
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #52c41a;">新增步骤</h4>
              <a-tag v-for="step in diffData.diffs.steps.added" :key="step.step_order" color="green">
                步骤{{ step.step_order }}
              </a-tag>
              <span v-if="!diffData.diffs.steps.added?.length" style="color: #999;">无</span>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #ff4d4f;">删除步骤</h4>
              <a-tag v-for="step in diffData.diffs.steps.removed" :key="step.step_order" color="red">
                步骤{{ step.step_order }}
              </a-tag>
              <span v-if="!diffData.diffs.steps.removed?.length" style="color: #999;">无</span>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="diff-section">
              <h4 style="color: #666;">未变步骤</h4>
              <a-tag v-for="step in diffData.diffs.steps.unchanged" :key="step.step_order">
                步骤{{ step.step_order }}
              </a-tag>
              <span v-if="!diffData.diffs.steps.unchanged?.length" style="color: #999;">无</span>
            </div>
          </a-col>
        </a-row>
      </a-card>

      <!-- 内容差异 (Monaco Editor Diff) -->
      <a-card title="内容差异" style="margin-top: 16px">
        <div ref="diffEditorContainer" class="diff-editor-container"></div>
      </a-card>
    </a-spin>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { SwapOutlined } from '@ant-design/icons-vue';
import * as monaco from 'monaco-editor';
import { compareKnowledgeVersions } from '../api/knowledge';

export default {
  name: 'KnowledgeVersionCompare',
  components: {
    SwapOutlined
  },
  setup() {
    const route = useRoute();
    const loading = ref(false);
    const diffData = ref(null);
    const diffEditorContainer = ref(null);
    let diffEditor = null;

    const version1 = computed(() => parseInt(route.query.v1) || 1);
    const version2 = computed(() => parseInt(route.query.v2) || 2);

    const fieldColumns = [
      { title: '字段', dataIndex: 'field', key: 'field', width: 120 },
      { title: '旧值', key: 'old_value', ellipsis: true },
      { title: '新值', key: 'new_value', ellipsis: true }
    ];

    const fieldNameMap = {
      title: '标题',
      type_id: '知识类型',
      content_format: '内容格式',
      sort_order: '排序',
      remark: '备注'
    };

    const changedFields = computed(() => {
      if (!diffData.value?.diffs?.fields) return [];
      return diffData.value.diffs.fields
        .filter(f => f.changed)
        .map(f => ({
          ...f,
          field: f.label || fieldNameMap[f.field] || f.field
        }));
    });

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatValue = (value) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    const initDiffEditor = () => {
      if (!diffEditorContainer.value) return;
      
      // 销毁旧的编辑器
      if (diffEditor) {
        diffEditor.dispose();
      }

      const oldContent = diffData.value?.diffs?.content?.old || '';
      const newContent = diffData.value?.diffs?.content?.new || '';

      diffEditor = monaco.editor.createDiffEditor(diffEditorContainer.value, {
        automaticLayout: true,
        readOnly: true,
        renderSideBySide: true,
        enableSplitViewResizing: true,
        originalEditable: false,
        scrollBeyondLastLine: false,
        minimap: { enabled: false },
        lineNumbers: 'on',
        wordWrap: 'on'
      });

      diffEditor.setModel({
        original: monaco.editor.createModel(oldContent, 'text/plain'),
        modified: monaco.editor.createModel(newContent, 'text/plain')
      });
    };

    const loadData = async () => {
      loading.value = true;
      try {
        const knowledgeId = route.params.id;
        const res = await compareKnowledgeVersions(knowledgeId, version1.value, version2.value);
        diffData.value = res.data;
        
        // 等待 DOM 更新后初始化编辑器
        setTimeout(() => {
          initDiffEditor();
        }, 100);
      } catch (error) {
        message.error('加载对比数据失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    watch([version1, version2], () => {
      loadData();
    });

    onMounted(() => {
      loadData();
    });

    onUnmounted(() => {
      if (diffEditor) {
        diffEditor.dispose();
      }
    });

    return {
      loading,
      diffData,
      diffEditorContainer,
      version1,
      version2,
      fieldColumns,
      changedFields,
      formatDate,
      formatValue
    };
  }
};
</script>

<style scoped>
.version-compare-container {
  padding: 16px;
}

.version-card.old-version :deep(.ant-card-head) {
  background: #fff1f0;
}

.version-card.new-version :deep(.ant-card-head) {
  background: #f6ffed;
}

.diff-editor-container {
  height: 500px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.diff-old {
  background: #ffa39e;
  padding: 2px 4px;
  border-radius: 2px;
  text-decoration: line-through;
}

.diff-new {
  background: #b7eb8f;
  padding: 2px 4px;
  border-radius: 2px;
}

.diff-section {
  padding: 8px;
  background: #fafafa;
  border-radius: 4px;
  min-height: 60px;
}

.diff-section h4 {
  margin-bottom: 8px;
  font-size: 13px;
}
</style>

